import { useState } from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/Button";
import { 
  Home,
  CheckCircle2,
  AlertCircle,
  ArrowRight
} from "lucide-react";
import ContactAgentPopup from "../components/ContactAgentPopup";
import { useAuth } from "../contexts/AuthContext";

const Opciones = () => {
  const { user } = useAuth();
  const [showContactAgentPopup, setShowContactAgentPopup] = useState(false);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden w-full">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-4 sm:px-6 overflow-hidden w-full">
        {/* Background gradient */}
        <div className="absolute inset-0 -z-10">
          <div className="w-full h-full bg-gradient-to-br from-background via-background/95 to-primary/10" />
        </div>
        
        {/* Animated circles */}
        <div className="absolute top-20 right-4 md:right-20 w-32 h-32 md:w-64 md:h-64 rounded-full bg-primary/20 blur-3xl animate-float opacity-50 md:opacity-100" />
        <div className="absolute bottom-20 left-4 md:left-20 w-48 h-48 md:w-96 md:h-96 rounded-full bg-accent/20 blur-3xl animate-float opacity-50 md:opacity-100" style={{ animationDelay: "2s" }} />

        <div className="container mx-auto max-w-6xl relative z-10 px-2 sm:px-4">
          <div className="text-center space-y-6">
            {/* HABILY Tag */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-primary/20">
              <Home className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="text-sm font-semibold text-primary">HABILY</span>
            </div>
            
            {/* Main Title */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold px-2 break-words">
              <span className="text-foreground">Vive hoy.</span>{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-primary">
                Compra cuando estés listo.
              </span>
            </h1>
            
            {/* Subtitle */}
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto px-2 break-words">
              Sin alquiler inflado. Sin promesas irreales. Sin letra pequeña.
            </p>
          </div>
        </div>
      </section>

      {/* Opciones Section */}
      <section className="py-12 px-4 sm:px-6 w-full overflow-x-hidden">
        <div className="container mx-auto max-w-6xl w-full">
          {/* Section Title */}
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 px-2 break-words">
            Tres formas inteligentes de acceder a tu futura casa
          </h2>
          
          {/* Three Option Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16 w-full">
            {/* Opción 1 */}
            <div className="glass p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border border-primary/10 hover:border-primary/30 transition-all duration-300 hover:glow-primary w-full overflow-hidden">
              {/* Tag */}
              <div className="inline-flex items-center px-3 py-1 rounded-full gradient-primary text-primary-foreground text-sm font-semibold mb-4 glow-primary">
                Opción 1
              </div>
              
              {/* Title */}
              <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3 break-words">
                Alquiler con Opción a Compra
              </h3>
              
              {/* Description */}
              <p className="text-muted-foreground mb-6 break-words">
                Ahorra mientras vives en tu futura casa. Parte del alquiler se destina a la compra.
              </p>
              
              {/* Benefits */}
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground break-words">Precio de compra fijado desde el inicio</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground break-words">Parte del alquiler se acumula para la compra</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground break-words">Tú decides si compras al final del plazo</span>
                </li>
              </ul>
              
              {/* Warning Note */}
              <div className="flex items-start gap-3 p-3 sm:p-4 bg-orange-500/10 dark:bg-orange-500/20 rounded-lg border border-orange-500/30">
                <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-orange-600 dark:text-orange-400 break-words">
                  El alquiler puede ser algo superior al de mercado
                </span>
              </div>
            </div>

            {/* Opción 2 */}
            <div className="glass p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border border-primary/10 hover:border-primary/30 transition-all duration-300 hover:glow-primary w-full overflow-hidden">
              {/* Tag */}
              <div className="inline-flex items-center px-3 py-1 rounded-full gradient-primary text-primary-foreground text-sm font-semibold mb-4 glow-primary">
                Opción 2
              </div>
              
              {/* Title */}
              <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3 break-words">
                Alquiler con Precio Garantizado
              </h3>
              
              {/* Description */}
              <p className="text-muted-foreground mb-6 break-words">
                Alquiler a precio de mercado con el precio de compra asegurado desde el día uno.
              </p>
              
              {/* Benefits */}
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground break-words">Alquiler a precio de mercado real</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground break-words">5% inicial asegura tu derecho de compra</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground break-words">Si no compras, sigues de alquiler sin penalización</span>
                </li>
              </ul>
              
              {/* Highlighted Benefit */}
              <div className="flex items-start gap-3 p-3 sm:p-4 bg-green-500/10 dark:bg-green-500/20 rounded-lg border border-green-500/30">
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium text-green-600 dark:text-green-400 break-words">
                  Máxima flexibilidad y libertad de decisión
                </span>
              </div>
            </div>

            {/* Opción 3 */}
            <div className="glass p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border border-primary/10 hover:border-primary/30 transition-all duration-300 hover:glow-primary w-full overflow-hidden md:col-span-2 lg:col-span-1">
              {/* Tag */}
              <div className="inline-flex items-center px-3 py-1 rounded-full gradient-primary text-primary-foreground text-sm font-semibold mb-4 glow-primary">
                Opción 3
              </div>
              
              {/* Title */}
              <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3 break-words">
                Búsqueda de la Mejor Hipoteca
              </h3>
              
              {/* Description */}
              <p className="text-muted-foreground mb-6 break-words">
                Compra cuando llegue el momento, con la financiación que realmente encaja contigo. Te ayudamos a comparar, negociar y elegir sin ir a ciegas.
              </p>
              
              {/* Benefits */}
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground break-words">Análisis de tu perfil financiero real</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground break-words">Comparativa entre bancos y condiciones actualizadas</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground break-words">Acompañamiento en la negociación con las entidades</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground break-words">Visión clara de cuotas, gastos y riesgos antes de firmar</span>
                </li>
              </ul>
              
              {/* Highlighted Benefit */}
              <div className="flex items-start gap-3 p-3 sm:p-4 bg-primary/10 dark:bg-primary/20 rounded-lg border border-primary/30">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium text-primary break-words">
                  Decisiones informadas antes de comprometerte
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* La diferencia HABILY Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-secondary/30 w-full overflow-x-hidden">
        <div className="container mx-auto max-w-6xl w-full">
          {/* Section Title */}
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-8 sm:mb-12 px-2 break-words">
            <span className="text-foreground">La diferencia</span>{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">
              HABILY
            </span>
          </h2>
          
          {/* Three Feature Cards */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-12 sm:mb-16 w-full">
            {/* Información clara */}
            <div className="glass p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-primary/10 hover:border-primary/30 transition-all duration-300 hover:glow-primary w-full overflow-hidden">
              <h3 className="text-lg sm:text-xl font-bold text-foreground mb-3 break-words">
                Información clara
              </h3>
              <p className="text-muted-foreground break-words">
                Te explicamos todo sin letra pequeña ni sorpresas.
              </p>
            </div>
            
            {/* Precio protegido */}
            <div className="glass p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-primary/10 hover:border-primary/30 transition-all duration-300 hover:glow-primary w-full overflow-hidden">
              <h3 className="text-lg sm:text-xl font-bold text-foreground mb-3 break-words">
                Precio protegido
              </h3>
              <p className="text-muted-foreground break-words">
                Te protegemos frente a subidas de precio del mercado.
              </p>
            </div>
            
            {/* Acompañamiento */}
            <div className="glass p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-primary/10 hover:border-primary/30 transition-all duration-300 hover:glow-primary w-full overflow-hidden sm:col-span-2 md:col-span-1">
              <h3 className="text-lg sm:text-xl font-bold text-foreground mb-3 break-words">
                Acompañamiento
              </h3>
              <p className="text-muted-foreground break-words">
                Te guiamos hasta que tenga sentido comprar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 w-full overflow-x-hidden">
        <div className="container mx-auto max-w-4xl text-center w-full">
          {/* Question */}
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 px-2 break-words">
            ¿Qué opción encaja contigo?
          </h2>
          
          {/* Description */}
          <p className="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto px-2 break-words">
            Analizamos tu situación y te decimos cuál es la mejor estrategia para tu caso. Sin compromiso.
          </p>
          
          {/* CTA Button */}
          <Button
            variant="hero"
            size="lg"
            onClick={() => setShowContactAgentPopup(true)}
            className="gap-2 w-full sm:w-auto px-4 sm:px-6 text-sm sm:text-base break-words"
          >
            <span className="break-words">Quiero saber qué opción es mejor para mí</span>
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Contact Agent Popup */}
      <ContactAgentPopup
        isOpen={showContactAgentPopup}
        onClose={() => setShowContactAgentPopup(false)}
        userEmail={user?.email}
        userName={user?.displayName}
      />
    </div>
  );
};

export default Opciones;
